import React, { useState, useEffect } from 'react';
import { initializeApp } from 'firebase/app';
import { getAuth, createUserWithEmailAndPassword, signInWithEmailAndPassword, onAuthStateChanged, signOut } from 'firebase/auth';
import { Users, LogOut, Mail, Lock, UserPlus, ShieldCheck, AlertTriangle } from 'lucide-react';

// --- CONFIGURAÇÃO DO FIREBASE ---
const firebaseConfig = {
  apiKey: "AIzaSyBQUZM5KfyN1ubp6YSb2EkHlerM_oFw1k4",
  authDomain: "organizador-e359a.firebaseapp.com",
  projectId: "organizador-e359a",
  storageBucket: "organizador-e359a.appspot.com",
  messagingSenderId: "258983333320",
  appId: "1:258983333320:web:c7516b9e37b949f27644fd",
  measurementId: "G-DFEQ9S82DY"
};

// --- URL DO SCRIPT DE E-MAIL ---
const SEND_EMAIL_URL = "https://admin.box100.com.br/api/send_email.php"; // Confirme se este URL está correto

// Inicializar um segundo app Firebase para o admin, para evitar conflitos de sessão
const adminApp = initializeApp(firebaseConfig, "adminApp");
const adminAuth = getAuth(adminApp);

// --- Componente de Login do Admin ---
const AdminLoginScreen = () => {    
    const [email, setEmail] = useState('');
    const [password, setPassword] = useState('');
    const [error, setError] = useState('');
    const [loading, setLoading] = useState(false);

    const handleLogin = (e) => {
        e.preventDefault();
        setLoading(true);
        setError('');
        signInWithEmailAndPassword(adminAuth, email, password)
            .catch((error) => {
                setError('Acesso de administrador inválido.');
                console.error("Admin Login Error:", error);
            })
            .finally(() => {
                setLoading(false);
            });
    };

    return (
        <div className="min-h-screen flex items-center justify-center bg-gray-100">
            <div className="max-w-md w-full bg-white shadow-md rounded-lg p-8 space-y-6">
                <div className="text-center">
                    <ShieldCheck className="mx-auto h-12 w-12 text-orange-600" />
                    <h2 className="mt-4 text-2xl font-bold text-gray-900">
                        Painel de Administração - Box100
                    </h2>
                </div>
                <form className="space-y-6" onSubmit={handleLogin}>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Email de Administrador</label>
                        <div className="mt-1 relative">
                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} required className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500" />
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700">Senha</label>
                        <div className="mt-1 relative">
                             <Lock className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={20} />
                            <input type="password" value={password} onChange={(e) => setPassword(e.target.value)} required className="w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500" />
                        </div>
                    </div>
                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}
                    <div>
                        <button type="submit" disabled={loading} className="w-full flex justify-center py-2 px-4 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-orange-600 hover:bg-orange-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-orange-500 disabled:bg-orange-300">
                            {loading ? 'A entrar...' : 'Entrar'}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

// --- Painel Principal do Admin ---
const AdminDashboard = ({ onLogout }) => {
    const [newEmail, setNewEmail] = useState('');
    const [newPassword, setNewPassword] = useState('');
    const [message, setMessage] = useState('');
    const [loading, setLoading] = useState(false);

    const handleCreateUser = async (e) => {
        e.preventDefault();
        setLoading(true);
        setMessage('');

        try {
            // Passo 1: Criar o utilizador no Firebase
            await createUserWithEmailAndPassword(adminAuth, newEmail, newPassword);
            
            // Passo 2: Tentar enviar o e-mail
            try {
                const emailResponse = await fetch(SEND_EMAIL_URL, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({ email: newEmail, senha: newPassword }),
                });
                const emailResult = await emailResponse.json();

                if (emailResult.success) {
                    setMessage(`Cliente "${newEmail}" criado e e-mail de boas-vindas enviado!`);
                } else {
                     setMessage(`ALERTA: Cliente "${newEmail}" criado, mas falha ao enviar e-mail. Erro: ${emailResult.error}`);
                }
            } catch (emailError) {
                setMessage(`ALERTA: Cliente "${newEmail}" criado, mas falha ao enviar e-mail. Verifique a consola (F12) para o erro de rede.`);
                console.error("Email Sending Error:", emailError);
            }

            setNewEmail('');
            setNewPassword('');

        } catch (error) {
            if (error.code === 'auth/email-already-in-use') {
                setMessage('Erro: Este email já está a ser utilizado.');
            } else {
                setMessage('Erro ao criar o cliente no Firebase.');
            }
            console.error("Create User Error:", error);
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="min-h-screen bg-gray-50">
            <header className="bg-white shadow-sm">
                <div className="max-w-7xl mx-auto py-4 px-4 sm:px-6 lg:px-8 flex justify-between items-center">
                    <h1 className="text-xl font-semibold text-gray-900">Gestão de Clientes</h1>
                    <button onClick={onLogout} className="flex items-center gap-2 text-sm font-medium text-gray-600 hover:text-orange-600">
                        <LogOut size={16} /> Sair
                    </button>
                </div>
            </header>
            <main>
                <div className="max-w-7xl mx-auto py-6 sm:px-6 lg:px-8">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                        <div className="md:col-span-1">
                            <div className="bg-white shadow rounded-lg p-6">
                                <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                                    <UserPlus size={20} /> Criar Novo Cliente
                                </h3>
                                <form onSubmit={handleCreateUser} className="mt-6 space-y-4">
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Email do Cliente</label>
                                        <input type="email" value={newEmail} onChange={(e) => setNewEmail(e.target.value)} required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500" />
                                    </div>
                                    <div>
                                        <label className="block text-sm font-medium text-gray-700">Senha Inicial</label>
                                        <input type="password" value={newPassword} onChange={(e) => setNewPassword(e.target.value)} required className="mt-1 w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-orange-500 focus:border-orange-500" />
                                    </div>
                                    <p className="text-xs text-gray-500">O cliente poderá alterar esta senha mais tarde.</p>
                                    <div>
                                        <button type="submit" disabled={loading} className="w-full bg-orange-600 text-white py-2 px-4 rounded-md hover:bg-orange-700 disabled:bg-orange-300">
                                            {loading ? 'A criar...' : 'Criar Cliente'}
                                        </button>
                                    </div>
                                    {message && <p className="mt-2 text-sm text-center text-gray-600">{message}</p>}
                                </form>
                            </div>
                        </div>
                        <div className="md:col-span-2">
                            <div className="bg-white shadow rounded-lg">
                                <div className="p-6">
                                    <h3 className="text-lg font-medium leading-6 text-gray-900 flex items-center gap-2">
                                        <Users size={20} /> Clientes Registados
                                    </h3>
                                    <div className="mt-4 bg-yellow-50 p-4 rounded-md text-sm text-yellow-800">
                                        <p className="flex items-start gap-2"><AlertTriangle size={20} className="flex-shrink-0" /> <strong>Nota de Segurança:</strong> A lista de utilizadores não é mostrada aqui por razões de segurança. A gestão completa (ver lista e apagar utilizadores) deve ser feita diretamente na <a href={`https://console.firebase.google.com/u/0/project/${firebaseConfig.projectId}/authentication/users`} target="_blank" rel="noopener noreferrer" className="font-bold text-orange-600 hover:underline">Consola do Firebase</a>.</p>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </main>
        </div>
    );
};

// --- Componente Principal do Painel de Admin ---
export default function App() {
    const [adminUser, setAdminUser] = useState(null);
    const [loading, setLoading] = useState(true);

    useEffect(() => {
        const unsubscribe = onAuthStateChanged(adminAuth, (user) => {
            setAdminUser(user);
            setLoading(false);
        });
        return () => unsubscribe();
    }, []);

    const handleLogout = () => {
        signOut(adminAuth);
    };

    if (loading) {
        return <div className="min-h-screen flex items-center justify-center"><p>A carregar...</p></div>;
    }

    if (!adminUser) {
        return <AdminLoginScreen />;
    }

    return <AdminDashboard onLogout={handleLogout} />;
}
